import React, { useCallback, MouseEventHandler, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { Button, Icons, Logo } from '@trr/common-component-library'
import FocusTrap from 'focus-trap-react'
import { INavigationRoute } from '@local/src/App.types'
import { useLockScroll } from '@local/src/Hooks'

import { IMenuProps } from './Menu.types'
import RoleMenu from './RoleMenu'
import SubMenu from './SubMenu'
import s from './Menu.module.scss'

const darkPurple = '#392b64'

const Menu: React.FunctionComponent<IMenuProps> = ({
  isVisible,
  hideMenu,
  routes = [],
  metaRoutes = [],
  testSelector,
}) => {
  const formatUrl = (url: string) => {
    url = url.toLocaleLowerCase()
    return url.endsWith('/') ? url.substring(0, url.length - 1) : url
  }

  const routeForUrl = (url: string, subRoutes: INavigationRoute[]) => {
    url = formatUrl(url)
    const sortedRoutes = subRoutes
      .slice()
      .sort((a: INavigationRoute, b: INavigationRoute) =>
        a.url.localeCompare(b.url)
      )
      .reverse()

    return sortedRoutes.filter((r) => url.startsWith(formatUrl(r.url)))[0]
  }

  const location = useLocation()

  const isRouteActive = (name: string) => {
    const activeRoleRoute = routeForUrl(location.pathname, routes)
    return activeRoleRoute?.name?.localeCompare(name) === 0
  }

  const onMouseDown: MouseEventHandler<HTMLElement> = useCallback((e) => {
    //In order to prevent focus state on click, but still allow it when tabbing for accessibility
    e.preventDefault()
  }, [])

  const activeRoute = routes.filter((r) => isRouteActive(r.name))[0]
  const inactiveRoutes = routes.filter((r) => !isRouteActive(r.name))
  const menuRef = useRef<HTMLDivElement>(null)

  const { enableScroll, disableScroll } = useLockScroll()

  useEffect(() => {
    if (menuRef?.current && isVisible) {
      menuRef.current.scrollTop = 0
      disableScroll()
    } else {
      enableScroll()
    }
  }, [isVisible, menuRef, enableScroll, disableScroll])

  return (
    <FocusTrap active={isVisible}>
      <nav
        role="navigation"
        id="nav"
        data-testid={testSelector}
        className={classNames(s.Menu, {
          [s.Visible]: isVisible,
        })}
      >
        {isVisible && (
          <div className={s.Top}>
            <Button
              customClass={s.CloseButton}
              onClick={hideMenu}
              icon
              attributes={{ tabIndex: 0, onMouseDown }}
            >
              <Icons.CloseIcon size={24} color={darkPurple} />
            </Button>
            <div className={s.LogoContainer}>
              <Logo
                color={darkPurple}
                style={{ marginLeft: 8, paddingLeft: 8 }}
              />
            </div>
          </div>
        )}
        <div
          className={classNames(s.MenuWrapper, {
            [s.Hidden]: !isVisible,
          })}
          ref={menuRef}
        >
          {activeRoute && (
            <SubMenu
              onClose={hideMenu}
              route={activeRoute}
              testSelector={`${testSelector}-main`}
            />
          )}
          <RoleMenu
            onClose={hideMenu}
            routes={inactiveRoutes}
            testSelector={`${testSelector}-roles`}
          />
          {metaRoutes.map((r) => (
            <React.Fragment key={`meta${r.name}`}>
              <SubMenu
                onClose={hideMenu}
                route={r}
                testSelector={`${testSelector}-meta`}
              />
            </React.Fragment>
          ))}
        </div>
        {/* TODO fix this */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={classNames(s.Overlay, {
            [s.OverlayVisible]: isVisible,
          })}
          onClick={hideMenu}
        />
      </nav>
    </FocusTrap>
  )
}

export default Menu
